import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { CallMediaPermissionValue, IMediaPermissions } from '@gem/call/CallStore/Call.types';
import * as callModel from '@gem/call/Model/callModel';
import {
  CallStatus,
  IStatusCallChanged,
  IWaitingRoomMember,
  PreviewDisplayMode,
  WaitingRoomAccessStatus,
  WaitingRoomMemberChangedStatus,
} from '@gem/call/Model/callModel.types';
import { wsCallsApiConnect, wsCallsApiDisconnect } from '@gem/call/WsApi/wsApi';
import { initCallsApi } from '@gem/call/WsApi/wsCallsApi/WsCallsApi';
import { IDevices } from '@gem/call/tasks/getDefaultDevices';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, popupsTasks } from '@gem/popups';
import { TAvatarBackgroundColorIndex } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { RefreshTokenInitiator } from 'APP/Tasks/authorization/login/login.types';
import { refreshToken } from 'APP/Tasks/authorization/login/refreshToken';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { RouterParams } from 'APP/router/constants';
import { useClearGetParameters } from 'APP/router/hooks/useClearGetParameters';
import useGetParameter from 'APP/router/hooks/useGetParameter';
import { websocketConfig } from 'APP/wsApi/wsApi.config';
import Entities from 'STORE';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';

interface IAvatarProps {
  url: string | null;
  title: string | null;
  colorIndex: TAvatarBackgroundColorIndex | null;
}

export interface IRoomCallPreviewPopupPresenter {
  ongoingCall?: MainCall | null;
  avatar: IAvatarProps;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  devicesIds: IDevices;
  waitingRoomMemberStatus: WaitingRoomMemberChangedStatus | null;
  isWaitingRoomStatusInitialized: boolean;
  isWaitingRoomEnabled: boolean | null;
  waitingRoomMembers: Map<string, IWaitingRoomMember>;
  previewDisplayMode: PreviewDisplayMode | null;
  isDarkMode: boolean;
  disposeRoomListenerFunc: (() => void) | null;
  isGuestsAllowed: boolean;
  mediaPermissions: IMediaPermissions;
  hasMediaPermissions: boolean;
  isPreviewInitialized: boolean;
  isShowDefaultPreview: boolean;
  isShowWebinarPreview: boolean;
  requestWaitingRoomAccess(status: WaitingRoomAccessStatus): void;
  disposeRoomListener(): void;
  onChangeAudioMuted(value: boolean): void;
  onMediaPermissionsChanged(value: IMediaPermissions): void;
  startCall(): void;
  onChangeVideoMuted(value: boolean): void;
  onChangeInitialDevicesIds(deviceIds: IDevices): void;
  startCallsSocket(channelId: string): void;
  stopCallsSocket(): void;
  onRefreshToken(): Promise<string | null>;
  checkToStart(): void;
  initWaitingRoom(): void;
  onStart(): void;
  onClose(stopSocket?: boolean): void;
  checkWaitingRoomMemberRejected(): void;
}

interface IRoomCallPreviewPopupPresenterProps {
  channelId: string;
}

export const useCallPreviewPresenter = (
  props: ICommonPopupsProps<IRoomCallPreviewPopupPresenterProps>
): IRoomCallPreviewPopupPresenter => {
  const { t } = useTranslation();
  const clearGetParameters = useClearGetParameters([RouterParams.roomCallChannelId], true);
  const roomCallChannelId = useGetParameter(RouterParams.roomCallChannelId);

  const presenter = useLocalObservable<IRoomCallPreviewPopupPresenter>(() => ({
    disposeRoomListenerFunc: null,
    mediaPermissions: {
      audio: CallMediaPermissionValue.Initial,
      video: CallMediaPermissionValue.Initial,
    },
    isWaitingRoomEnabled: null,
    waitingRoomMembers: new Map(),
    waitingRoomMemberStatus: null,
    isWaitingRoomStatusInitialized: false,
    isGuestsAllowed: true,
    isAudioMuted: true,
    isVideoMuted: true,
    devicesIds: {
      audioinput: null,
      audiooutput: null,
      videoinput: null,
    },
    previewDisplayMode: null,

    get isDarkMode(): boolean {
      return Entities.appearance.isDark;
    },

    get hasMediaPermissions(): boolean {
      return (
        presenter.mediaPermissions.audio !== CallMediaPermissionValue.Initial &&
        presenter.mediaPermissions.video !== CallMediaPermissionValue.Initial
      );
    },

    get ongoingCall(): MainCall | null {
      return Entities.Calls.ongoingCall;
    },

    get avatar(): IAvatarProps {
      const me = Entities.usersStore.Me;

      return { url: me.avatarUrl, title: me.avatarTitle, colorIndex: me.avatarColorIndex };
    },

    get isPreviewInitialized(): boolean {
      return presenter.previewDisplayMode !== null;
    },

    get isShowDefaultPreview(): boolean {
      return presenter.previewDisplayMode === PreviewDisplayMode.DefaultPreview;
    },

    get isShowWebinarPreview(): boolean {
      return presenter.previewDisplayMode === PreviewDisplayMode.WebinarPreview;
    },

    onChangeVideoMuted(value: boolean): void {
      presenter.isVideoMuted = value;
    },

    onChangeAudioMuted(value: boolean): void {
      presenter.isAudioMuted = value;
    },

    startCall(): void {
      Tasks.calls.startCall({
        channelId: props.params!.channelId!,
        withAudio: !presenter.isAudioMuted,
        withVideo: !presenter.isVideoMuted,
        devicesIds: presenter.devicesIds,
        mediaPermissions: presenter.mediaPermissions,
        waitingRoomMembers: Array.from(presenter.waitingRoomMembers.values()),
        isWaitingRoomEnabled: presenter.isWaitingRoomEnabled || false,
        isGuestsAllowed: presenter.isGuestsAllowed,
      });
      presenter.onClose(false);
    },

    startCallsSocket(channelId: string): void {
      wsCallsApiConnect({
        session: websocketConfig.token || '',
        channelId,
        wsCallsUrl: ProjectUrls.wsCallsUrl,
        refreshToken: async () => presenter.onRefreshToken(),
      });
    },

    stopCallsSocket(): void {
      wsCallsApiDisconnect();
    },

    async onRefreshToken(): Promise<string | null> {
      const result = await refreshToken(false, RefreshTokenInitiator.WsCalls);

      if (result.isSuccess && result.token) {
        return result.token;
      } else {
        return null;
      }
    },

    requestWaitingRoomAccess(status: WaitingRoomAccessStatus): void {
      callModel.requestWaitingRoomAccess({
        channelId: props.params!.channelId!,
        status,
      });
    },

    async onStart(): Promise<void> {
      if (Entities.Calls.ongoingCall || Entities.ActiveStream.hasStream) {
        if (
          Entities.Calls.ongoingCall?.channelId &&
          props.params?.channelId &&
          Entities.Calls.ongoingCall.channelId === props.params.channelId
        ) {
          presenter.onClose();
          return;
        }

        popupsTasks.showAlert(t('calls_join_another_call'));
        return;
      }

      const isWaitingRoomApproved =
        presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Approved;
      const canStartCall = isWaitingRoomApproved || !presenter.isWaitingRoomEnabled;

      if (canStartCall) {
        presenter.startCall();
        return;
      }

      presenter.requestWaitingRoomAccess(WaitingRoomAccessStatus.AskToJoin);
    },

    checkToStart(): void {
      if (presenter.isWaitingRoomEnabled === null || presenter.waitingRoomMemberStatus === null) {
        return;
      }

      if (!presenter.isWaitingRoomStatusInitialized) {
        presenter.isWaitingRoomStatusInitialized = true;
        return;
      }

      if (
        presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Approved
      ) {
        presenter.startCall();
      }

      if (
        !presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Requested
      ) {
        presenter.startCall();
      }
    },

    checkWaitingRoomMemberRejected(): void {
      if (
        presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Rejected
      ) {
        popupsTasks.showAlert({
          title: t('common_request_rejected'),
          text: t('calls_waiting_room_request_declined_message'),
          primaryText: t('common_ok'),
        });
        presenter.onClose();
      }
    },

    initWaitingRoom(): void {
      initCallsApi(ProjectUrls.wsCallsUrl);
      presenter.disposeRoomListenerFunc = callModel.initWaitingRoomCallListener({
        onStatusCallChanged(data: IStatusCallChanged) {
          if (data.status === CallStatus.Finished) {
            popupsTasks.showAlert({
              title: t('call_finished'),
            });
            presenter.onClose();
          }
          presenter.isWaitingRoomEnabled = data.waitingRoomEnabled;
          presenter.isGuestsAllowed = data.guestsAllowed;
          presenter.previewDisplayMode =
            data.uiFeatures?.previewDisplayMode || PreviewDisplayMode.DefaultPreview;

          presenter.checkWaitingRoomMemberRejected();
        },
        onWaitingRoomChanged: (data) => {
          presenter.waitingRoomMembers = data.requested.reduce((acc, member) => {
            if (member.status === WaitingRoomMemberChangedStatus.Requested) {
              acc.set(member.memberId, member);
            }
            return acc;
          }, new Map());
          presenter.checkToStart();
        },
        onWaitingRoomMemberChanged: (data) => {
          if (data.memberId === Entities.usersStore.Me.id) {
            presenter.waitingRoomMemberStatus = data.status;
            presenter.checkToStart();
          } else if (data.status === WaitingRoomMemberChangedStatus.Requested) {
            presenter.waitingRoomMembers.set(data.memberId, data);
          } else {
            presenter.waitingRoomMembers.delete(data.memberId);
          }

          presenter.checkWaitingRoomMemberRejected();
        },
      });
      presenter.startCallsSocket(props.params!.channelId!);
    },

    disposeRoomListener(): void {
      if (presenter.disposeRoomListenerFunc) {
        presenter.disposeRoomListenerFunc();
        presenter.disposeRoomListenerFunc = null;
      }
    },

    onChangeInitialDevicesIds(props: IDevices): void {
      presenter.devicesIds = props;
    },

    onMediaPermissionsChanged(value: IMediaPermissions): void {
      presenter.mediaPermissions = {
        ...presenter.mediaPermissions,
        ...value,
      };
    },

    onClose(stopSocket = true): void {
      Entities.Calls.setHasWaitingRoomRequest(false);
      if (
        presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Requested
      ) {
        presenter.requestWaitingRoomAccess(WaitingRoomAccessStatus.Cancel);
      }
      presenter.disposeRoomListener();
      if (stopSocket) {
        presenter.stopCallsSocket();
      }
      props.popupInstance.close();
      props.onClose?.();
      clearGetParameters();
    },
  }));

  useEffect(() => {
    presenter.initWaitingRoom();
    Entities.Calls.setHasWaitingRoomRequest(true);
  }, []);

  useEffect(() => {
    if (!roomCallChannelId) {
      presenter.onClose();
    }
  }, [roomCallChannelId]);

  return presenter;
};
